import { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import useNonInitialEffect from '@versiondos/hooks';
import { Lot as LotModel, Product } from '@spike/product-model';
import { ProductsStatus, saveLotThunk } from '@spike/products-action';
import { RootState } from 'store';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import NumberField from 'components/UI/V3/Forms/NumberField';
import { Switch } from 'components/UI/V3/Forms/Switch';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import { ProductHeader } from '../StockSidebar/ProductHeader';
import { QuantityStepper } from './QuantityStepper';
import moment, { Moment } from 'moment-timezone';
import clsx from 'clsx';
import stock from '../StockSidebar/StockSidebar.module.scss';
import styles from './LotsSidebar.module.scss';

interface LotFormProps {
    product: Product;
    lot?: LotModel;
    onBack: () => void;
    onSaved: () => void;
    onStateChange?: (state: { canSave: boolean; saving: boolean }) => void;
}

export interface LotFormHandle {
    save: () => void;
    canSave: boolean;
    saving: boolean;
}

export const LotForm = forwardRef<LotFormHandle, LotFormProps>(
    ({ product, lot, onBack, onSaved, onStateChange }, ref) => {
        const dispatch = useDispatch();
        const apiClientWrapper = useApiClientWrapper();
        const marketplace = useMarketplace();
        const timeZone = marketplace.schedule.timeZone;
        const status = useSelector<RootState, ProductsStatus>(state => state.products.status);
        const isEdit = lot != null;
        const businessProductId = product.businessProduct?.id;

        const initialExpirationDate = useMemo(() => {
            if (!lot?.expirationDate) {
                return null;
            }
            const date = moment.tz(lot.expirationDate, timeZone);
            return date.isValid() ? date.startOf('day') : null;
        }, [lot?.expirationDate, timeZone]);

        const [lotNumber, setLotNumber] = useState(lot?.lotNumber ?? '');
        const [expires, setExpires] = useState(!!initialExpirationDate);
        const [expirationDate, setExpirationDate] = useState<Moment | null>(initialExpirationDate);
        const [showCalendar, setShowCalendar] = useState(false);
        const [quantity, setQuantity] = useState(lot?.quantity && lot.quantity > 0 ? lot.quantity : 10);
        const [unitCost, setUnitCost] = useState<number | undefined>(lot?.unit_cost);

        const saving = status === ProductsStatus.SavingLot;
        const canSave = quantity >= 1 && !saving && businessProductId != null;
        const minExpirationDate = useMemo(() => {
            const tomorrow = moment().tz(timeZone).add(1, 'day').startOf('day');
            if (initialExpirationDate && initialExpirationDate.isBefore(tomorrow)) {
                return initialExpirationDate.clone();
            }
            return tomorrow;
        }, [timeZone, initialExpirationDate]);

        useNonInitialEffect(() => {
            if (status === ProductsStatus.SaveLotSuccess) {
                onSaved();
            }
        }, [status]);

        useEffect(() => {
            onStateChange?.({ canSave, saving });
        }, [canSave, saving, onStateChange]);

        const handleSave = () => {
            if (!canSave || businessProductId == null) {
                return;
            }

            const payload: LotModel = {
                id: lot?.id,
                uuid: lot?.uuid ?? apiClientWrapper.uuid(),
                marketplaceId: lot?.marketplaceId ?? apiClientWrapper.marketplaceId,
                businessProductId,
                lotNumber: lotNumber.trim() || undefined,
                expirationDate: expires && expirationDate ? expirationDate.format('YYYY-MM-DD') : undefined,
                quantity,
                destroyable: lot?.destroyable ?? true,
                unit_cost: unitCost
            };

            dispatch(saveLotThunk(apiClientWrapper, payload));
        };

        useImperativeHandle(ref, () => ({
            save: handleSave,
            canSave,
            saving
        }));

        const handleExpiresChange = (checked: boolean) => {
            setExpires(checked);
            if (!checked) {
                setExpirationDate(null);
                setShowCalendar(false);
            } else if (!expirationDate) {
                setExpirationDate(minExpirationDate.clone());
                setShowCalendar(true);
            }
        };

        return (
            <>
                <div className={stock.detailsHeader}>
                    <button
                        type="button"
                        className={stock.backButton}
                        onClick={onBack}
                        aria-label="Back to lots"
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <h2 className={stock.detailsTitle}>{isEdit ? 'Edit Lot' : 'Add Lot'}</h2>
                </div>
                <ProductHeader
                    product={product}
                    trackingLabel="Track by Lots"
                />
                <div className={clsx(stock.field, styles.lotNumberField)}>
                    <Input
                        id="lot-number"
                        label="Lot Number"
                        placeholder="Enter lot number"
                        value={lotNumber}
                        maxLength={24}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setLotNumber(event.target.value)}
                        disabled={saving}
                        autoFocus
                    />
                </div>
                <div className={stock.quantityRow}>
                    <span className={stock.quantityLabel}>Quantity</span>
                    <QuantityStepper
                        quantity={quantity}
                        disabled={saving}
                        onChange={setQuantity}
                    />
                    <span className={stock.newStockChip}>New Stock: {quantity}</span>
                </div>
                <div className={styles.expiresRow}>
                    <span className={stock.quantityLabel}>Expires</span>
                    <Switch
                        checked={expires}
                        onChange={handleExpiresChange}
                        disabled={saving}
                    />
                    {expires && (
                        <button
                            type="button"
                            className={styles.dateButton}
                            onClick={() => setShowCalendar(open => !open)}
                            disabled={saving}
                        >
                            {expirationDate ? expirationDate.format('dddd, D MMM YYYY') : 'Select date'}
                            <FontAwesomeIcon icon={faChevronDown} />
                        </button>
                    )}
                </div>
                {expires && showCalendar && (
                    <div className={styles.calendarWrap}>
                        <DatePicker
                            timeZone={timeZone}
                            value={expirationDate}
                            minDate={minExpirationDate}
                            onChange={date => {
                                setExpirationDate(date);
                                setShowCalendar(false);
                            }}
                        />
                    </div>
                )}
                <div className={clsx(stock.field, stock.unitCostField)}>
                    <NumberField
                        id="lot-unit-cost"
                        name="unitCost"
                        label="Unit Cost"
                        value={unitCost}
                        onChange={setUnitCost}
                        scale={2}
                        precision={6}
                        allowEmptyValue
                        disabled={saving}
                        startIcon={<span>$</span>}
                        className={stock.unitCostInput}
                        placeholder="0.00"
                    />
                </div>
            </>
        );
    }
);

LotForm.displayName = 'LotForm';

export default LotForm;
